import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../utils/api';
import { invalidateAllUserCaches, invalidateUpcomingMatchesCache } from '../utils/cacheUtils';

interface User {
  id: string;
  email: string;
  teamName: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  role: 'user' | 'admin';
  lastLogin?: string;
  favouriteTeam?: {
    id: string;
    name: string;
    shortName?: string;
    logoUrl?: string;
  };
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
  preferences: {
    defaultLanguage: string;
    timezone: string;
    emailNotifications: boolean;
  };
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: User) => void;
  loading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  teamName: string;
  firstName?: string;
  lastName?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Update token in localStorage for API client interceptor
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await apiClient.get('/auth/me');
          setUser(response.data.user);
          // Clear upcoming matches cache when automatically logging in with existing token
          console.log('Auto-login detected - clearing upcoming matches cache...');
          invalidateUpcomingMatchesCache();
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      // Clear upcoming matches cache to show personalized matches for logged-in user
      console.log('User logged in - clearing upcoming matches cache...');
      invalidateUpcomingMatchesCache();
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      
      // Clear upcoming matches cache to show personalized matches for new user
      console.log('User registered - clearing upcoming matches cache...');
      invalidateUpcomingMatchesCache();
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  /**
   * Logout function that clears all user-related caches and authentication data
   * 
   * Clears the following caches:
   * - Upcoming matches (Home page)
   * - Leagues and user leagues
   * - TeamWidget global rank data
   * - User predictions and statistics
   * - News and posts
   * - Leaderboards and partial leaderboards
   * - Seasons data
   * - Trophy awards
   * 
   * Preserves non-user-specific caches like:
   * - Scroll positions
   * - Fanleague groupname
   */
  const logout = () => {
    console.log('Logging out - clearing user caches...');
    
    // Clear all user-related caches from sessionStorage
    invalidateAllUserCaches();
    
    console.log('User caches cleared successfully');
    
    // Clear authentication data
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 